import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Injector } from '@angular/core';
import { map } from 'rxjs/operators';
import { createHttpParams } from '~/infrastructure/http/params';
import { augmentPaging } from '~/infrastructure/paging';
import { StockItemListing, StockItemListingWithKitDetails, StockItemListingWithPoDetails, StockItemListingWithReceiveDetails, StockItemListingWithType, StockItemSummary } from '~/models/modules/item';
import { ApiServiceBase, API_NAMES } from '~/services/api';
import { unitOfMeasureTypes } from '~/shared/enums/unitOfMeasureTypes';

@Injectable({ providedIn: 'root' })
export class StockItemsService extends ApiServiceBase {

  isAddKitting = new EventEmitter<any>();

  constructor(injector: Injector, private http: HttpClient) {
    super(injector);
    this.setBase(API_NAMES.stocks);
  }

  getStockItem(id: number) {
    return this.http.get<StockItemSummary>(this._url(`/${id}`));
  }

  getStockItems(paramsObj?: any, model?: any, screenSiteId?: number) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    paramsObj.screenSiteId = screenSiteId;
    let params = createHttpParams(paramsObj);

    if (model && !model.iskit) {
      model.iskit = false;
    }

    return this.http.post<StockItemSummary[]>(this._url(`/grid-data`), model, { params, observe: 'response' }).pipe(map(augmentPaging));
  }

  getstockItemListingWithPaging(screenSiteId?: number, paramsObj?: any) {
    paramsObj = paramsObj || {};
    paramsObj.screenSiteId = screenSiteId;
    let params = createHttpParams(paramsObj);

    return this.http.get<StockItemListing[]>(this._url(`/dropdownlist-data-paging`), {
      params,
      observe: 'response',
    });
  }

  GetStockItemByUnitOfMeasureId(unitOfMeasureId: number, unitOfMeasureType: unitOfMeasureTypes) {
    return this.http.get<StockItemSummary[]>(this._url(`/um-data/${unitOfMeasureId}/${unitOfMeasureType}`), { observe: 'response' }).pipe(map(augmentPaging));
  }

  GetStockItemByAddressId(addressId: number, model: any) {
    return this.http.post<StockItemSummary[]>(this._url(`/Addresses/${addressId}/grid-data`), model, { observe: 'response' }).pipe(map(augmentPaging));
  }

  GetStockItemByLocationType(locationTypeId: number) {
    return this.http.get<StockItemSummary[]>(this._url(`/LocationTypes/${locationTypeId}/grid-data`), { observe: 'response' }).pipe(map(augmentPaging));
  }

  GetStockItemByLocationId(locationId: number, model: any, paramsObj?: any) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    let params = createHttpParams(paramsObj);

    return this.http.post<StockItemSummary[]>(this._url(`/Locations/${locationId}/grid-data`), model, { params, observe: 'response' }).pipe(map(augmentPaging));
  }

  getStockItemsListingBySite(siteId: number, paramsObj?: any) {
    let params = createHttpParams(paramsObj);
    return this.http.get<StockItemListingWithType[]>(this._url(`/site/${siteId}`), {
      params,
      observe: 'response',
    });
  }

  getStockItemsForBill(siteId?: number) {
    if (siteId == null) {
      siteId = 0;
    }

    return this.http.get<any[]>(this._url(`/getitemsforbill/${siteId}`), {
      observe: 'response',
    });
  }

  getStockItemsListing(siteId: number) {
    return this.http.get<StockItemListing[]>(this._url(`/siteListing/${siteId}`));
  }

  getStockItemsListingBySiteWithKitDetails(siteId: number, paramsObj?: any) {
    let params = createHttpParams(paramsObj);

    return this.http.get<StockItemListingWithKitDetails[]>(this._url(`/site/withkitdetails/${siteId}`), {
      params,
      observe: 'response',
    });
  }

  getStockItemsListingBySiteWithPoDetails(siteId: number, paramsObj?: any) {
    let params = createHttpParams(paramsObj);

    if (siteId == null) {
      siteId = 0;
    }

    return this.http.get<StockItemListingWithPoDetails[]>(this._url(`/site/withpodetails/${siteId}`), {
      params,
      observe: 'response',
    });
  }

  getStockItemsListingBySiteWithReceiveDetails(siteId: number, paramsObj?: any) {
    let params = createHttpParams(paramsObj);

    return this.http.get<StockItemListingWithReceiveDetails[]>(this._url(`/site/withreceivedetails/${siteId}`), {
      params,
      observe: 'response',
    });
  }
  getStockItemsListingFortransfer(model: any, paramsObj?: any) {
    let params = createHttpParams(paramsObj);

    return this.http.post<StockItemListingWithReceiveDetails[]>(this._url(`/site/transfer-items/`), model, { params, observe: 'response', });
  }

  getAccountItemTypes(model: any) {
    return this.http.post<any>(this._url(`/itemtypesaccounts`), model);
  }

  getAccountCostTypes(model: any) {
    return this.http.post<any>(this._url(`/costtypesaccounts`), model);
  }

  createStockItem(model: any) {
    return this.http.post<string>(this._url(), model);
    //  return this.http.post('https://localhost:44325/api/modules/stocks/Items',model)
  }

  getStockItemAlternate(code: string) {
    // var res = this.client.get(this._url(`/GetStockItemAlternate/${code}`))

    return this.client.get(this._url(`/GetStockItemAlternate/${code}`));
  }

  updateStockItem(id: number, model: any) {
    return this.client.put(this._url(`/${id}`), model);
  }

  deleteStockItem(id: number) {
    return this.client.delete(this._url(`/${id}`));
  }

  deactivateStockItem(obj: any) {
    return this.client.put(this._url(`/deactivate`), obj);
  }

  deactivateStockBatch(obj: any) {
    return this.client.put(this._url(`/batchDeactivate`), obj);
  }

  getItemPerPOChart(id: number) {
    return this.client.get(this._url(`/ItemPerPOChart/${id}`));
  }

  getItemPerSOChart(id: number) {
    return this.client.get(this._url(`/ItemPerSOChart/${id}`));
  }

  getItemQtyPerLocation(id: number) {
    return this.client.get(this._url(`/ItemQtyPerLocation/${id}`));
  }

  getMinMax(id: number) {
    return this.client.get(this._url(`/min-max/${id}`));
  }

  getQuantityPerDate(id: number) {
    return this.client.get(this._url(`/quantity-per-date/${id}`));
  }

  getStockHistory(id: number) {
    return this.client.get(this._url(`/stock-history/${id}`));
  }

  getImageDirectoryAndPrimaryImageStream(id: number) {
    return this.client.get(this._url(`/image-url/${id}`));
  }

  reactivateStockItem(model: any) {
    return this.client.put(this._url(`/reactivate`), model);
  }

  reactivateStockItemBatch(obj: any) {
    return this.client.put(this._url(`/reactivate-batch`), obj);
  }

  getUpcInfo(modelNumber: string) {
    return this.client.get(this._url(`/UpcInfo/${modelNumber}`));
  }

  getStockItemQuantityMinMaxChart(stockItemId: number) {
    return this.client.get(this._url(`/StockItems/${stockItemId}/StockQuantityMinMaxChart`));
  }

  getSoPoStockItemChart(stockItemId: number) {
    return this.client.get(this._url(`/StockItems/${stockItemId}/Po/So/Chart`));
  }

  getQuantityPerLocationChart(stockItemId: number) {
    return this.client.get(this._url(`/StockItems/${stockItemId}/QuantityPerLocationChart`));
  }

  getStockHistoryChart(stockItemId: number) {
    return this.client.get(this._url(`/StockItems/${stockItemId}/HistoryChart`));
  }

  getStockItemQuantityByCategory(categoryId: number, model: any) {
    return this.http.post<StockItemSummary[]>(this._url(`/Categories/${categoryId}/grid-data`), model, { observe: 'response', }).pipe(map(augmentPaging));
  }

  GetStockItemsQunatityOnHandPerCategory(top: number = 10) {
    return this.client.get(this._url(`/GetStockItemsQunatityOnHandPerCategory/${top}`));
  }

  GetStockItemsCountPerCategory(top: number = 10) {
    return this.client.get(this._url(`/GetStockItemsCountPerCategory/${top}`));
  }
  //#region  GetStockItemNumberPerQuantityOnHand
  GetStockItemNumberPerQuantityOnHand(top: number = 10) {

    return this.client.get(this._url(`/GetStockItemNumberPerQuantityOnHand/${top}`));
  }
  //#endregion

  GetInventoryTypePerQuantityOnHand(top: number = 10) {
    return this.client.get(this._url(`/GetInventoryTypePerQuantityOnHand/${top}`));
  }

  GetStockItemNumberQuantityBelowMinimum(top: number = 10) {
    return this.client.get(this._url(`/GetStockItemNumberQuantityBelowMinimum/${top}`));
  }

  GetStockItemNumberQuantityAboveMaximum(top: number = 10) {
    return this.client.get(this._url(`/GetStockItemNumberQuantityAboveMaximum/${top}`));
  }

  GetStockItemsCountPerCostType(top: number = 10) {
    return this.client.get(this._url(`/GetStockItemsCountPerCostType/${top}`));
  }

  GetStockItemsQuantityOnHandPerCostType(top: number = 10) {
    return this.client.get(this._url(`/GetStockItemsQuantityOnHandPerCostType/${top}`));
  }

  GetStockItemQuantityOnHandPerCostRange(top: number = 10) {
    return this.client.get(this._url(`/GetStockItemQuantityOnHandPerCostRange/${top}`));
  }

  GetStockItemQuantityOnHandPerPriceRange(top: number = 10) {
    return this.client.get(this._url(`/GetStockItemQuantityOnHandPerPriceRange/${top}`));
  }

  ToggleShopping(itemId: number) {
    return this.client.get(this._url(`/StockItems/ToggleShopping/${itemId}`));
  }

  ToggleFlag(itemId: number) {
    return this.client.get(this._url(`/StockItems/ToggleFlag/${itemId}`));
  }

  private _url(v?: string | undefined) {
    v = v || '';
    return this.url(`/Items${v}`);
  }
}
